import { Component, OnInit, ElementRef, ViewChild, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { DialogService } from '../../services/dialog.service';
import { DocumentManagementService, IUploadFile, IAzureBlobSasToken } from '../../services/document-management.service';
import { LayoutService } from '../../../layouts/services/layout.service';
import { LoaderService } from '../../services/loader.service';
import { Subscription, Observable, Subject, forkJoin, of } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { finalize, concatMap, tap } from 'rxjs/operators';

@Component({
    selector: 'upload-view-files',
    templateUrl: './upload-view-files.component.html',
    styleUrls: ['./upload-view-files.component.scss'],
    standalone: false
})
export class UploadViewFilesComponent implements OnInit, OnDestroy {

    @Input() uniqueId: string = 'file';
    @Input() coInstanceId: string = null;
    @Input() isSupplemental: boolean = false;
    @Input() componentLabel: string = "Attach Supplemental Files";
    @Input() isPadded: boolean = true;
    @Input() public allowUpload: boolean = true;
    @Input() public allowDeletion: boolean;
    @Input() showFileList: boolean = true;
    @Input() enabled: boolean = false;
    @Output() fileRemoved: EventEmitter<IUploadFile> = new EventEmitter<IUploadFile>();
    @Output() newFilesList = new EventEmitter<IUploadFile>();
    @ViewChild("input") public fileInput: ElementRef;

	private _whitelist: string[] = [];
	private whitelistMessage = "You have uploaded a disallowed file type.  The file upload has been cancelled. <br /> The allowed file types are: <000>. <br /> NOTE: RICS is no longer accepting Word 97-2003 (.doc) format files. <br />  Please select a different file and try again.  "


    public hasFilesToDrop: boolean = false;
    public fileSubscription = new Subscription();
    public file: IUploadFile;
    private _files: Array<IUploadFile> = new Array<IUploadFile>();
    get files(): Array<IUploadFile> {
        if (this._files == null) this._files = [];
        this._files = this._files.filter(this.onlyUnique);
        return this._files;
    }

    @Input() set files(value: Array<IUploadFile>) {
        this._files = value;
    }
    public fileToSave: Array<IUploadFile> = new Array<IUploadFile>();

    constructor(public dialogService: DialogService,
        private docMgmtSrvc: DocumentManagementService,
        public layoutService: LayoutService,
        private readonly loader: LoaderService,
        private authSrv: AuthService,
        public _el: ElementRef) { }
        

    ngOnInit() {
    }

    ngOnDestroy() {
        this.fileSubscription.unsubscribe();
    }

    /**
* Handle style update when dragging over.
* @param event
*/
    public onDragOver(event: DragEvent): boolean {
        this.hasFilesToDrop = true;
        return false;
    }

    /**
    * Handle style update when dragging out.
    * @param event
    */
    public onDragLeave(event: DragEvent): boolean {
        event.preventDefault();
        this.hasFilesToDrop = false;
        return false;
    }

    private onlyUnique(value, index, self) {
        return self.indexOf(value) === index;
    }

    /**
    * Handle Drop of files.
    * @param event
    */
    public onDrop(event: DragEvent): void {
        event.preventDefault();
        this.hasFilesToDrop = false;
		let whitelistObs$: Observable<string[]> = (this._whitelist.length > 0)
			? of(this._whitelist)
			: this.docMgmtSrvc.whiteList.pipe(tap((data) => { this._whitelist = data; }))
		let files = event.dataTransfer.files;
		this.fileSubscription.add(whitelistObs$.subscribe(
			(list) => {
				if (list && list.length > 0) {
					const extensions = [];
					for (let fi = 0; fi < files.length; fi++) {
						const parts = files[fi].name.split(".");
						if (parts.length > 1) {
							extensions.push(parts[parts.length - 1]);
						} else {
							extensions.push("");
						}
					}
					const result = extensions.every(f => list.indexOf(f.toLowerCase()) !== -1);
					if (result) {
						this.addFilesToQueue(files);
					} else {
						let message = this.whitelistMessage.replace("<000>", this._whitelist.join(', '));
						this.dialogService.openDialog("ok", message, "disallowed");
					}
				}
			}, (err) => {
				console.error(err);
				this.dialogService.openDialog("ok", "There was an issue trying to upload the file you specified. If this continues, please try again at a later time.", "error");
			}));
    }
    public addFiles(event: Event): void {        
        const files = (event.srcElement as any).files;
        if (files) {
			this.loader.toggleLoader(true);
			let whitelistObs$: Observable<string[]> = (this._whitelist.length > 0) 
				? of(this._whitelist)
				: this.docMgmtSrvc.whiteList.pipe(tap((data) => { this._whitelist = data; }))
			this.fileSubscription.add(whitelistObs$.pipe(
            ).subscribe(list => {
                if (list && list.length > 0) {
                    const extensions = [];
                    for (let fi = 0; fi < files.length; fi++) {
                        const parts = files[fi].name.split(".");
                        if (parts.length > 1) {
                            extensions.push(parts[parts.length - 1]);
                        } else {
                            extensions.push("");
                        }
                    }

                    const result = extensions.every(f => list.indexOf(f.toLowerCase()) !== -1);
                    this.loader.toggleLoader(false);
                    if (result) {
                        this.addFilesToQueue(files);
					} else {
						let message = this.whitelistMessage.replace("<000>", this._whitelist.join(', '));
						this.dialogService.openDialog("ok", message, "disallowed");
                    }
                    //Need to wipe out the value of the input so that should they remove the item via the separate delete button and attempt to reupload it right after,
                    //The change event will still fire.
                    this.fileInput.nativeElement.value = "";
                }
			}, (error) => {
				this.loader.toggleLoader(false);
				this.dialogService.openDialog("ok", "There was an issue trying to upload the file you specified. If this continues, please try again at a later time.", "error");
			}));
        }
    }

    public chooseFileLabelClick(event: Event) {
        event.stopPropagation();
        this.fileInput.nativeElement.click();
    }

    public addFilesToQueue(files: FileList): void {
        for (let i = 0; i < files.length; i++)
            if (!this._fileExists(files[i]))
                this.files.push({ rawFile: files[i], completed: false, waiting: true, isUploading: false, percentage: 0, fileName: files.item(i).name, type: files.item(i).type, uploadedByUserId: this.authSrv.User.id, uploadedByOrganizationId: this.authSrv.User.organizationId } as IUploadFile);
        this.processFiles();
    }

    public _fileExists(file: File): boolean {
        for (var f of this.files) {
            if (this.filesMatch(f, file)) {
                return true;
            }
        }
        return false;
    }
    public processFiles(): void {
        if (!this.files.length)
            return;
        const files = this.files.filter(x => !x.completed && !x.isUploading);
        if (files.length === 0)
            return;
        let obs: Observable<IAzureBlobSasToken>[] = [];
        files.forEach(x => {
            obs.push(this.uploadFile(x));
        });
        this.loader.toggleLoader(true);
        this.fileSubscription.add(forkJoin(obs).subscribe(results => { this.loader.toggleLoader(false); }, (error) => { this.loader.toggleLoader(false); }));
    }

    private filesMatch(firstFile: IUploadFile, secondFile: File): boolean {
        let match = false;

        if (firstFile.fileName === secondFile.name)
            match = true;

        return match;
    }

    public uploadFile(file: IUploadFile): Observable<IAzureBlobSasToken> {
        const uploadComplete = new Subject<IAzureBlobSasToken>();

        let provision$ = this.docMgmtSrvc.provision(file.rawFile.name).pipe(concatMap((x) => {
            let successObservable = this.docMgmtSrvc.saveProvisionedFile(file, x, uploadComplete, this.coInstanceId, this.isSupplemental);
            return successObservable;
            },
        ));
        this.loader.toggleLoader(true);
        let provisionedSave$ = provision$.pipe(finalize(() => { this.fileSubscription.add(provisionedSave$); })
        ).subscribe(
            provisionSuccess => {
                if (provisionSuccess)
                    this.newFilesList.emit(file);
                else {

                    //Upload failed, remove file from list of files
                    const index = this.files.findIndex(x => this.filesMatch(x, file.rawFile));
                    if (index !== -1)
                        this.files.splice(index, 1);

                    //Now pop dialog informing user of failure
                    this.dialogService.openDialog("ok", "There was an issue trying to upload the file you specified. If this continues, please try again at a later time.", "error");
                }
                this.loader.toggleLoader(false);
            },
            (error) => {
                this.loader.toggleLoader(false);
				if (this.docMgmtSrvc.WHITELIST_ERRORS.indexOf(error.error) !== -1) {
					let message = this.whitelistMessage.replace("<000>", this._whitelist.join(', '));
					this.dialogService.openDialog("ok", message, "disallowed");
                }
                else
                    this.dialogService.openDialog("ok", "There was an issue trying to upload the file you specified. If this continues, please try again at a later time.", "error");

            });

        return uploadComplete.asObservable();
    }

    public removeFile(file: IUploadFile): void {
        const index = this.files.findIndex(x => x.blobUri === file.blobUri);
        if (index !== -1) {
            this.files.splice(index, 1);
            this.fileRemoved.emit(file);
        }
    }

    public onDialogConfirm(): void {
        this.dialogService.closeDialog();
    }

    public onDialogClose(): void {
        this.dialogService.closeDialog();
    }
}